import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  BookOpen,
  CalendarCheck,
  ClipboardList,
  Clock3,
  CreditCard,
  DoorOpen,
  FileText,
  Gauge,
  LayoutDashboard,
  LibraryBig,
  LogOut,
  Menu,
  Receipt,
  Settings as SettingsIcon,
  ShieldCheck,
  Users,
  UsersRound,
  Wallet,
  X,
} from "lucide-react";
import { Logo } from "./Primitives";
import { api } from "../lib/api";

const groupsFor = (role) => {
  if (role === "MAIN_ADMIN") {
    return [
      {
        label: "Overview",
        items: [
          { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" },
        ],
      },
      {
        label: "Tenants",
        items: [
          { id: "libraries", label: "Libraries", icon: LibraryBig, to: "/libraries" },
          { id: "subscriptions", label: "Subscriptions", icon: Gauge, to: "/subscriptions" },
          { id: "invoices", label: "Billing", icon: Receipt, to: "/invoices" },
        ],
      },
      {
        label: "Platform",
        items: [
          { id: "notifications", label: "Notifications", icon: Bell, to: "/notifications" },
          { id: "audit", label: "Activity logs", icon: FileText, to: "/audit" },
          { id: "settings", label: "Settings", icon: SettingsIcon, to: "/settings" },
        ],
      },
    ];
  }
  return [
    {
      label: "Overview",
      items: [{ id: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" }],
    },
    {
      label: "Operations",
      items: [
        { id: "students", label: "Students", icon: Users, to: "/students" },
        { id: "attendance", label: "Attendance", icon: CalendarCheck, to: "/attendance" },
        { id: "seats", label: "Seats", icon: DoorOpen, to: "/seats" },
        { id: "shifts", label: "Shifts", icon: Clock3, to: "/shifts" },
        { id: "books", label: "Books", icon: BookOpen, to: "/books" },
      ],
    },
    {
      label: "Finance",
      items: [
        { id: "payments", label: "Payments", icon: Wallet, to: "/payments" },
        { id: "bills", label: "Bills", icon: CreditCard, to: "/bills" },
      ],
    },
    {
      label: "Control",
      items: [
        { id: "staff", label: "Staff", icon: UsersRound, to: "/staff" },
        { id: "reports", label: "Reports", icon: ClipboardList, to: "/reports" },
        { id: "notifications", label: "Notifications", icon: Bell, to: "/notifications" },
        { id: "settings", label: "Settings", icon: SettingsIcon, to: "/settings" },
      ],
    },
  ];
};

const canSee = (user, id) => {
  if (user.role !== "STAFF" || id === "dashboard") return true;
  return (user.permissions?.[id] || []).length > 0;
};

export default function AppShell({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const groups = groupsFor(user.role)
    .map((group) => ({ ...group, items: group.items.filter((item) => canSee(user, item.id)) }))
    .filter((group) => group.items.length > 0);
  const showNotifications = canSee(user, "notifications");
  const current = location.pathname.split("/")[1] || "dashboard";

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!showNotifications) return undefined;
    let active = true;
    const load = () =>
      api
        .get("/notifications")
        .then((response) => {
          if (active) setUnread(response.data.unread || 0);
        })
        .catch(() => {});
    load();
    const interval = setInterval(load, 60000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
    navigate("/");
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`} data-testid="app-sidebar">
        <div className="sidebar-top">
          <Logo />
          <button
            type="button"
            className="icon-button sidebar-close"
            onClick={() => setOpen(false)}
            aria-label="Close navigation"
            data-testid="sidebar-close-button"
          >
            <X size={20} />
          </button>
        </div>
        <div className="workspace-selector">
          <span className="workspace-avatar">{(user.name || "L").charAt(0)}</span>
          <span>
            <strong>{user.role === "MAIN_ADMIN" ? "LibraryOS HQ" : user.library?.name || "Library workspace"}</strong>
            <small>{user.role.replace(/_/g, " ").toLowerCase()}</small>
          </span>
          <ShieldCheck size={16} />
        </div>
        <nav className="side-nav" data-testid="sidebar-nav">
          {groups.map((group) => (
            <div key={group.label}>
              <p>{group.label}</p>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
                    data-testid={`sidebar-${item.id}-link`}
                  >
                    <Icon size={18} />
                    {item.label}
                    {item.id === "notifications" && unread ? <span className="badge-count">{unread}</span> : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="user-row">
            <span className="user-avatar">{(user.name || "L").charAt(0)}</span>
            <span>
              <strong>{user.name}</strong>
              <small>{user.role.replace(/_/g, " ").toLowerCase()}</small>
            </span>
          </div>
          <button type="button" className="side-link logout-link" onClick={logout} data-testid="logout-button">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>
      <div className="shell-main">
        <header className="app-header">
          <button
            type="button"
            className="icon-button menu-button"
            onClick={() => setOpen(true)}
            aria-label="Open navigation"
            data-testid="sidebar-menu-button"
          >
            <Menu size={21} />
          </button>
          <div className="breadcrumb">
            <span>Workspace</span>
            <span>›</span>
            <strong>{current.charAt(0).toUpperCase() + current.slice(1)}</strong>
          </div>
          <div className="header-actions">
            {showNotifications ? (
              <Link to="/notifications" className="icon-button notif-button" aria-label="Notifications" data-testid="notifications-button">
                <Bell size={19} />
                {unread ? <span className="notif-dot" /> : null}
              </Link>
            ) : null}
            <div className="status-dot">
              <span /> Data synced
            </div>
            <div className="header-avatar">{(user.name || "L").charAt(0)}</div>
          </div>
        </header>
        <main className="app-content">
          <Outlet />
        </main>
        <footer className="app-footer">Powered by MKS Digital Technology · LibraryOS workspace</footer>
      </div>
    </div>
  );
}
